import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { DashboardFiltersFacade } from '../../domain/+state/dashboard-filters.facade';
import { DashboardFacade } from '../../domain/+state/dashboard.facade';
import { DashboardFilters } from '../../domain/model/dashboard-filters.model';
import { DashboardVmState } from '../../domain/model/dashboard-vm.model';
import { SmartInsights, SmartInsightsDataVm, SmartInsightsVm } from '../../domain/model/smart-insights.model';
import {
  ConversionSelection,
  sameConversionSelection,
  toConversionSelection,
} from '../../shared/conversion-metrics.helper';
import { companyToday, resolveDateRange } from '../../shared/dashboard-filters.helper';
import { clampToNow, errorVm, loadingVm } from '../../shared/dashboard-vm.helper';
import { toSmartInsightRowVm } from '../../shared/smart-insights.helper';

const LOADING_VM: SmartInsightsVm = loadingVm<SmartInsightsDataVm>();

/** Rows in the server's order; each label reads its insight's own comparison window. */
function toDataVm(insights: SmartInsights): SmartInsightsDataVm {
  const rows = insights.insights.map(toSmartInsightRowVm);
  return { rows, isEmpty: rows.length === 0 };
}

/**
 * Screen facade of the smart insights page: one request per distinct `(from, to, scope, period)` and a
 * precomputed view model. Component-provided; lives as long as the screen.
 */
@Injectable()
export class SmartInsightsFacade {
  /** loading → data | error per request. */
  readonly vm$: Observable<SmartInsightsVm>;

  constructor(
    private readonly dashboardFacade: DashboardFacade,
    private readonly filtersFacade: DashboardFiltersFacade,
  ) {
    // The range is resolved from the same emission as the period: pairing `filters$` with `dateRange$` would
    // emit the new period with the old range first, a request the server rejects.
    this.vm$ = combineLatest([this.filtersFacade.filters$, this.filtersFacade.now$]).pipe(
      map(([filters, now]: [DashboardFilters, Date]) =>
        toConversionSelection(filters, resolveDateRange(filters, companyToday(now))),
      ),
      filter((selection): selection is ConversionSelection => selection !== null),
      distinctUntilChanged(sameConversionSelection),
      switchMap((selection: ConversionSelection) => this.load(selection)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /** Errors are caught here so one failed request never ends `vm$`; the next filter change fetches again. */
  private load(selection: ConversionSelection): Observable<SmartInsightsVm> {
    const response$ = this.dashboardFacade
      .getSmartInsights$(selection.range, selection.scope, selection.period)
      .pipe(map((insights: SmartInsights) => ({ asOfUtc: insights.asOfUtc, data: toDataVm(insights) })));
    return combineLatest([response$, this.filtersFacade.now$]).pipe(
      map(
        ([response, now]): SmartInsightsVm => ({
          state: DashboardVmState.DATA,
          asOfUtc: clampToNow(response.asOfUtc, now),
          errorMessage: null,
          data: response.data,
        }),
      ),
      startWith(LOADING_VM),
      catchError((error: unknown) => of(errorVm<SmartInsightsDataVm>(error))),
    );
  }
}
